import type { ComponentProps, JSX } from 'react'
import { InputText } from 'primereact/inputtext'
import { Dropdown } from 'primereact/dropdown'
import { InputTextarea } from 'primereact/inputtextarea'
import { FormField } from './FormField.js'

interface FieldChrome {
  label?: string | undefined
  required?: boolean | undefined
  error?: string | undefined
  help?: string | undefined
  fieldClassName?: string | undefined
}

/**
 * FormInput — FormField + PrimeReact InputText merged into one call, so a
 * labelled text field is one component instead of a wrapper + a control.
 */
export function FormInput({
  label,
  required,
  error,
  help,
  fieldClassName,
  id,
  className = '',
  ...rest
}: FieldChrome & { id: string } & Omit<ComponentProps<typeof InputText>, 'id'>): JSX.Element {
  return (
    <FormField label={label} htmlFor={id} required={required} error={error} help={help} className={fieldClassName}>
      <InputText id={id} className={`w-full ${error ? 'p-invalid' : ''} ${className}`} {...rest} />
    </FormField>
  )
}

/**
 * FormSelect — FormField + PrimeReact Dropdown merged into one call.
 */
export function FormSelect({
  label,
  required,
  error,
  help,
  fieldClassName,
  id,
  className = '',
  ...rest
}: FieldChrome & { id: string } & Omit<ComponentProps<typeof Dropdown>, 'id'>): JSX.Element {
  return (
    <FormField label={label} htmlFor={id} required={required} error={error} help={help} className={fieldClassName}>
      <Dropdown id={id} className={`w-full ${error ? 'p-invalid' : ''} ${className}`} {...rest} />
    </FormField>
  )
}

/**
 * FormTextarea — FormField + PrimeReact InputTextarea merged into one call.
 */
export function FormTextarea({
  label,
  required,
  error,
  help,
  fieldClassName,
  id,
  className = '',
  ...rest
}: FieldChrome & { id: string } & Omit<ComponentProps<typeof InputTextarea>, 'id'>): JSX.Element {
  return (
    <FormField label={label} htmlFor={id} required={required} error={error} help={help} className={fieldClassName}>
      <InputTextarea id={id} className={`w-full ${error ? 'p-invalid' : ''} ${className}`} {...rest} />
    </FormField>
  )
}
